import * as React from 'react';

// Material-UI
import BlockIcon from '@mui/icons-material/Block';
import { amber, blueGrey, green, red } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Grid from '@mui/material/Grid';
import PanToolIcon from '@mui/icons-material/PanTool';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  RequestGuest
} from '../../models';

import {
  EMPTY_STRING,
  SignupStatusID
} from '../../global';

// UTC to local date formatting hooks
import { useDates } from '../../hooks/UseDates';

interface RequestSummaryGuestsProps {
  guests: Array<RequestGuest>;
}

const RequestSummaryGuests: React.FC<RequestSummaryGuestsProps> = (props) => {

  const { dateStringToLocalDateFormatted } = useDates();

  const [hasPriority, setHasPriority] = React.useState<boolean>(false);

  // #region Styles

  const theme = useTheme();
  const styles = {
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    guestsContainer: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    pending: {
      color: blueGrey[500]
    },
    waitlist: {
      color: amber[500]
    }
  };

  // #endregion

  // #region Methods

  /*
* Render the request status icon
*/
  const renderStatus = (statusID: number, status: string) => {

    switch (statusID) {
      case SignupStatusID.Requested:
        return (
          <Tooltip title={status} arrow>
            <PendingIcon fontSize="medium" sx={styles.pending} />
          </Tooltip>
        )
      case SignupStatusID.Confirmed:
        return (
          <Tooltip title={status} arrow>
            <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
          </Tooltip>
        )
      case SignupStatusID.Waitlist:
        return (
          <Tooltip title={status} arrow>
            <PanToolIcon fontSize="medium" sx={styles.waitlist} />
          </Tooltip>
        )
      case SignupStatusID.Denied:
        return (
          <Tooltip title={status} arrow>
            <BlockIcon fontSize="medium" sx={styles.deined} />
          </Tooltip>
        )
      default:
        return (<span />)
    }

  }

  /*
 * Render members 
 */
  const renderGuests = () => {

    let gs: Array<React.JSX.Element> = [];

    props.guests.forEach(g => {
      gs.push(
        <Grid container size={12} spacing={1} alignItems="center">
          <Grid size={1}>
            {renderStatus(g.statusID, g.status)}
          </Grid>
          <Grid size={6}>
            <Typography variant="body2">{g.lastName || EMPTY_STRING}, {g.firstName || EMPTY_STRING}{g.priorityGuest ? '*' : EMPTY_STRING} ({g.gender})-{g.guestType}</Typography>
          </Grid>
          <Grid size={5}>
            <Typography variant="body2">{dateStringToLocalDateFormatted(g.arrivalDate, 'EEEEEE MM/dd')} - {dateStringToLocalDateFormatted(g.departureDate, 'EEEEEE MM/dd')} ({g.nights} nights)</Typography>
          </Grid>
        </Grid>
      );
    });

    return gs;
  }

  React.useEffect(() => {
    setHasPriority(props.guests.some(g => g.priorityGuest));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.guests.length]);

  // #endregion

  return (
    <Grid container size={12} sx={styles.guestsContainer}>
      <Grid container size={12}>
        <Typography variant="button">Guests</Typography>
      </Grid>
      <Grid container size={12}>
        {renderGuests()}
      </Grid>
      {hasPriority &&
        <Grid container size={12}>
          <Typography variant="caption">*=Priority Guest</Typography>
        </Grid>
      }
    </Grid>
  );

}

export default RequestSummaryGuests;